import { type WireTypes } from '@flakiness/server/common/wireTypes.js';
import { TypedHTTP } from '@flakiness/shared/common/typedHttp.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType, provide } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import ms from 'ms';
import { api, snapshotGoneTarget } from '../api.js';
import { CommitRegistry } from '../commitRegistry.js';
import { consume, contexts, ContextTask, FKDataSnapshotRefresh, FKProjectChanged } from '../contexts.js';
import { SNAPSHOT_ID_PARAM } from '../platformUrl.js';
import { URLStateRouter } from '../router.js';
import { linkStyles } from './cssstyles.js';
import { OrganizationContext } from './organization-context.js';

@customElement('project-context')
export class ProjectContext extends LitElement {

  static render(options: {
    orgSlug?: string,
    projectSlug?: string,
    projectPublicId?: string,
  }, e: TemplateResult) {
    // Re-use the same TemplateResult so that the resulting element
    // could be efficiently rerendered by clients.
    return OrganizationContext.render({ orgSlug: options.orgSlug }, html`
      <project-context
        .orgSlug=${options.orgSlug}
        .projectSlug=${options.projectSlug}
        .projectPublicId=${options.projectPublicId}
      >${e}</project-context>`);
  }

  @property({ attribute: false }) orgSlug?: string;
  @property({ attribute: false }) projectSlug?: string;

  @consume({ context: contexts.notLoggedIn, subscribe: true }) @state() private _notLoggedIn?: ContextType<typeof contexts.notLoggedIn>;  

  @state() private _snapshotGone = false;
  // A snapshot requested via the `snapshot_id` URL param, e.g. by a cmd-click
  // from another tab. Used for the first snapshot fetch only.
  private _snapshotIdFromURL = (new URLSearchParams(window.location.search).get(SNAPSHOT_ID_PARAM) || undefined) as WireTypes.DataSnapshotId|undefined;

  // Links inside a snapshot carry its id so that cmd-clicks open the same
  // snapshot. Strip it from the address bar on every navigation, so that
  // reloads and copied URLs get the latest data.
  // This works for both in-app navigations and new-tab navigations.
  private _urlStateRouter = new URLStateRouter(this, params => {
    if (params.has(SNAPSHOT_ID_PARAM)) {
      const url = new URL(window.location.href);
      url.searchParams.delete(SNAPSHOT_ID_PARAM);
      window.history.replaceState(window.history.state, '', url);
    }
  });

  private _onSnapshotGone = () => this._snapshotGone = true;

  private _snapshotStatusInterval?: NodeJS.Timeout;

  override connectedCallback() {
    super.connectedCallback();
    snapshotGoneTarget.addEventListener('gone', this._onSnapshotGone);
    this._snapshotStatusInterval = setInterval(() => {
      // Neither status can change back to 'current', so there is nothing left to poll for.
      // If an outdated snapshot goes missing, the next request for it gets a 410
      // and opens the "out of date" dialog.
      const status = this._dataSnapshotInfo.status;
      if (status === 'outdated' || status === 'missing')
        return;
      this._dataSnapshotInfoTask.run();
    }, ms('1 minute'));
  }

  override disconnectedCallback() {
    snapshotGoneTarget.removeEventListener('gone', this._onSnapshotGone);
    clearInterval(this._snapshotStatusInterval);
    this._snapshotGone = false;
    this._dataSnapshotId = undefined;
    this._dataSnapshotInfo = {};
    super.disconnectedCallback();
  }

  @provide({ context: contexts.commitRegistry }) private _commitRegistry = new CommitRegistry();
  @provide({ context: contexts.dataSnapshotId }) private _dataSnapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @provide({ context: contexts.dataSnapshotInfo }) private _dataSnapshotInfo: ContextType<typeof contexts.dataSnapshotInfo> = {};

  private _projectTask = new ContextTask(this, contexts.project, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      this._commitRegistry.clear();
      return await api.project.findProject.GET({ orgSlug, projectSlug }, { signal });
    }
  });

  private _dataSnapshotIdTask = new Task(this, {
    args: () => [this._projectTask.value] as const,
    task: async ([project], { signal }) => {
      CHECK(project);
      // We initiate a complete refresh if the snapshot is gone.
      const previousInfo = this._snapshotGone ? {} : this._dataSnapshotInfo;
      this._snapshotGone = false;

      this._dataSnapshotInfo = {
        ...previousInfo,
        loadingMode: previousInfo.snapshotId ? 'refresh' : 'initial',
        loadingStarted: Date.now(),
        loadingFinished: undefined,
      };
      const result = await api.snapshot.get.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId: this._snapshotIdFromURL,
      }, { signal });
      CHECK(result);
      // Clear the snapshot after the first successful fetch.
      this._snapshotIdFromURL = undefined;
      // `previousInfo` predates the stamp above, so carry the start time
      // through explicitly rather than letting the spread restore the old one.
      this._dataSnapshotInfo = {
        ...this._dataSnapshotInfo,
        snapshotId: result.snapshotId,
        createdAt: result.createdAt,
        status: result.status,
        loadingFinished: Date.now(),
      };
      this._dataSnapshotId = result.snapshotId;
      return result.snapshotId;
    },
  });

  private _dataSnapshotInfoTask = new Task(this, {
    args: () => [this._projectTask.value, this._dataSnapshotId] as const,
    task: async ([project, snapshotId], { signal }) => {
      CHECK(project && snapshotId);
      const result = await api.snapshot.status.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
      }, { signal });
      // A status request for the previous snapshot can finish while a refresh is
      // in progress. Do not let it replace information about the new snapshot.
      if (this._dataSnapshotInfo.snapshotId !== snapshotId )
        return;
      this._dataSnapshotInfo = {
        ...this._dataSnapshotInfo,
        status: result.status,
      };
      if (result.status === 'missing')
        this._snapshotGone = true;
    },
  });

  private _projectPullsTask = new ContextTask(this, contexts.projectPulls, {
    args: () => [this._projectTask.value, this._dataSnapshotId] as const,
    task: async ([project, snapshotId], { signal }) => {
      CHECK(project && snapshotId);
      const result = await api.project.pulls.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        prState: 'open',
        testState: 'all',
        sortOptions: {
          axis: 'created',
          direction: 'desc',
        },
        pageOptions: {
          number: 0,
          size: 10,
        },
      }, { signal }).catch(e => {
        console.error(e);
        throw e;
      });
      CHECK(result);
      return result;
    },
  });

  private _projectReferencesTask = new ContextTask(this, contexts.projectReferences, {
    args: () => [this._projectTask.value, this._dataSnapshotId] as const,
    task: async ([project, snapshotId], { signal }) => {
      CHECK(project && snapshotId);
      const result = await api.project.branches.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        pageOptions: {
          number: 0,
          size: 10,
        },
      }, { signal }).catch(e => {
        console.error(e);
        throw e;
      });
      CHECK(result);
      return {
        defaultBranch: result.defaultBranch,
        branches: result.branches,
      };
    },
  });

  private _testStatusTask = new ContextTask(this, contexts.testsStatus, {
    args: () => [this._projectTask.value, this._dataSnapshotId] as const,
    task: async ([project, snapshotId]) => {
      CHECK(project && snapshotId);
      const result = await api.report.counters.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
      });
      return result.testOutcomes;
    }
  });

  override render(): unknown {
    const err = this._projectTask.render({
      error: (error: any) => error,
    });
    const isNotFound = err?.status === TypedHTTP.StatusCodes.ClientErrors.NOT_FOUND;
    const isPaymentRequired = err?.status === TypedHTTP.StatusCodes.ClientErrors.PAYMENT_REQUIRED;
    const loginRequired = this._notLoggedIn && isNotFound;

    return html`
      <sl-dialog
        no-header
        label="Project data is out of date"
        ?open=${this._snapshotGone}
        @sl-request-close=${(event: Event) => event.preventDefault()}
      >
        <v-box>
          <h2>Project data is out of date</h2>
          <div>This data snapshot is no longer available. Refresh to load the latest project data.</div>
        </v-box>
        <sl-button slot=footer variant=primary @click=${() => this._dataSnapshotIdTask.run()}>Refresh</sl-button>
      </sl-dialog>
      <section @fk-data-snapshot-refresh=${(event: FKDataSnapshotRefresh) => {
        this._dataSnapshotIdTask.run();
      }} @fk-project-changed=${(event: FKProjectChanged) => {
        if (event.orgSlug.toLowerCase() !== this.orgSlug?.toLowerCase() ||
            event.projectSlug.toLocaleLowerCase() !== this.projectSlug?.toLowerCase())
          return;
        this._projectTask.run();
      }}>
        ${loginRequired ? html`<page-login></page-login>` :
          isPaymentRequired ? html`<page-http-error code=402></page-http-error>` : 
          isNotFound ? html`<page-http-error code=404></page-http-error>` : html`<slot></slot>`}
      </section>
    `;
  }

  static styles = [linkStyles, css`
    :host, section {
      display: contents;
    }
  `];
}
